import { Editor, EditorTheme } from "./editor.js";
import * as THREE from 'three';
import * as TWEEN from '@tweenjs/tween.js';
import { Viewport } from "./viewport.js";
import { AddObjectCommand } from "./commands/AddObjectCommand.js";
import * as compoennts from "./components.js";
import { MaterialType, PandaMode } from "./enums.js";
import { Keyboard } from "./keyboard.js";
//import { EditorCamera } from "./camera.js";
import {
  Euler,
  Material,
  MaterialParameters,
  MeshBasicMaterial,
  MeshLambertMaterial,
  MeshStandardMaterial,
  Object3D, OrthographicCamera,
  Sprite, TubeGeometry,
  Vector2, Vector3
} from "three";
import { Player } from "./player.js";
import { SetMaterialCommand } from "./commands/SetMaterialCommand.js";
import {compress, decompress} from "./index.js";
import { ViewportInfo } from "./viewport.info.js";
import { SetValueCommand } from "./commands/SetValueCommand.js";
import {RectAreaLightUniformsLib} from "three/examples/jsm/lights/RectAreaLightUniformsLib";

import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import {SetMaterialValueCommand} from "./commands/SetMaterialValueCommand";
import {SetPositionCommand} from "./commands/SetPositionCommand";
import {SetRotationCommand} from "./commands/SetRotationCommand";
import {SetScaleCommand} from "./commands/SetScaleCommand";
import {MoveObjectCommand} from "./commands/MoveObjectCommand";
import {createPath} from "./utils/util";
import {SetGeometryCommand} from "./commands/SetGeometryCommand";
import {LineBasicMaterialParameters} from "three/src/materials/LineBasicMaterial";

const DATA_TRANSFER_KEY='panda_object';

export interface PandaOptions{
  /**
   * 模式 默认: edit
   */
  mode?:PandaMode;

  /**
   * 编辑器主题
   */
  theme?:EditorTheme;

  /**
   * 选中物体辅助颜色
   */
  selectColor?:THREE.ColorRepresentation;

  /**
   * class
   */
  className?:string;

  pixelRatio?:number;
  /**
   * 控制器属性
   *
   */
  control?: Control

  /**
   * 是否显示辅助对象
   */
  showHelper?:boolean;
  /**
   * 是否显示网格
   */
  showGrid?:boolean;

  /**
     * 渲染精度 shader precision. Can be "highp", "mediump" or "lowp".
     */
   precision?: string | undefined;

   /**
    * default is false.
    */
   alpha?: boolean | undefined;

   /**
    * default is true.
    */
   premultipliedAlpha?: boolean | undefined;

   /**
    * 抗锯齿 default is false.
    */
   antialias?: boolean | undefined;

   /**
    * default is true.
    */
   stencil?: boolean | undefined;

   /**
    * default is false.
    */
   preserveDrawingBuffer?: boolean | undefined;

   /**
    * Can be "high-performance", "low-power" or "default"
    */
   powerPreference?: string | undefined;

   /**
    * default is true.
    */
   depth?: boolean | undefined;

   /**
    * default is false.
    */
   logarithmicDepthBuffer?: boolean | undefined;

   /**
    * default is false.
    */
   failIfMajorPerformanceCaveat?: boolean | undefined;
  /**
   * 孪生源配置
   */
   twinConfig?: TwinConfig | undefined;
}


const defaultPandaOptions:PandaOptions={
  mode:PandaMode.Edit,
  theme:EditorTheme.System,
  className:'',
  pixelRatio:window.devicePixelRatio,
  showHelper:true,
  showGrid:true,
  selectColor:0x409eff,
}


export class PandaEngine {
  el:HTMLElement;
  container:HTMLElement;
  options:PandaOptions;
  private components?:PandaCommponents=defaultComponents;
  editor:Editor;
  private viewport:Viewport;
  private renderer:THREE.WebGLRenderer;
  private keyboard?:Keyboard;

  /**
   * 添加物体之前钩子函数
   */
  public onAddObjectBefore?: (compoennt: PandaCommponent, object: THREE.Object3D) => boolean;
  /**
   * 添加物体之后钩子函数
   */
  public onAddObjectAfter?: (compoennt: PandaCommponent, object: THREE.Object3D) => void;

  constructor(el:HTMLElement,options?:PandaOptions) {
    RectAreaLightUniformsLib.init();
    this.el=el;
    this.options=Object.assign(defaultPandaOptions,options);
    this.container=document.createElement('div');
    this.container.className=`panda ${this.options.mode} ${this.options.className}`;
    this.container.style.position='relative';
    this.container.style.width='100%';
    this.container.style.height='100%';
    this.el.appendChild(this.container);

    this.editor=new Editor(this.options);
    if(this.options.theme){
      this.editor.theme=this.options.theme;
    }
    if(this.options.showHelper!==undefined){
      this.editor.signals.showHelpersChanged.dispatch(this.options.showHelper);
    }
    if(this.options.showGrid!==undefined){
      this.editor.signals.showGridChanged.dispatch(this.options.showGrid);
    }
    if (this.options.twinConfig){
      this.editor.twinConfig = this.options.twinConfig
    }

    this.renderer = new THREE.WebGLRenderer({antialias: this.options.antialias ?? true ,alpha: this.options.alpha ?? false });
    this.renderer.setSize( el.clientWidth, el.clientHeight );
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.VSMShadowMap

    this.editor.initEffectComposer(el,this.renderer)

    //创建视图
    this.viewport=new Viewport(this.editor,this.options);
    this.viewport.mode=this.options.mode||PandaMode.Edit;
    this.container.appendChild(this.viewport.dom);
    this.container.appendChild(this.viewport.stats.dom);
    this.viewport.dom.addEventListener('click',()=>{
      this.viewport.dom.focus();
    })

    const info=ViewportInfo(this.editor);
    this.container.appendChild(info);

    // 创建预览视图
    const player=Player(el,this.editor);
    player.style.top='0px';
    player.style.left='0px';
    player.style.width='100%';
    player.style.height='100%';

    this.container.appendChild(player);

    //执行画布渲染
    this.editor.signals.rendererCreated.dispatch(this.renderer);

    if(this.options.mode===PandaMode.Edit){
      this.keyboard=new Keyboard(this.editor);
      this.container.addEventListener('dragover',this.onDragover.bind(this));
      this.container.addEventListener('drop',this.onDrop.bind(this));
    }else if(this.options.mode===PandaMode.Run){
      this.startPlayer();
    }

    window.addEventListener( 'resize', this.onWindowResize.bind(this) );
    this.onWindowResize();
  }

  private onWindowResize(){
    this.editor.signals.windowResize.dispatch();
  }
  private onDragover(e:DragEvent){
    e.stopPropagation();
    e.preventDefault();
  }
  private onDrop(e:DragEvent){
    const json=e.dataTransfer?.getData(PandaEngine.DargData);
    if(json){
      const data=JSON.parse(json);
      if (!data.position) {
        let pos = this.get_mouse_plane_pos(e)
        pos.setY(0)
        data.position = pos
      }
      this.add(data);
      this.viewport.dom.focus();
    }
  }

  loadHDR(url:string){
    const hdrLoader=new RGBELoader();

    hdrLoader.loadAsync(url).then((texture)=>{
      // 设置天空球面的纹理映射
      // 三等距柱状投影反射映射告诉渲染器环境映射采用等距柱状投影格式。由于着色器仅支持非 PBR 的立方体贴图格式和 PBR 材质的立方体 UV 格式，因此必须转换等距柱状投影纹理才能进行渲染。这是由渲染器自动完成的。
      texture.mapping=THREE.EquirectangularReflectionMapping;
      this.editor.signals.sceneBackgroundChanged.dispatch(texture);
      this.editor.signals.sceneEnvironmentChanged.dispatch(texture);
    });
  }
  /**
   * 设置视图 2D或3D
   * @param showSceneHelpers
   */
  setViewDirection(type:string){
    if (type === '2d'){
      this.viewport.controls.controls.enableRotate = false;
      this.viewport.controls.controls.object.lookAt(new Vector3(0, 0, 0))
      this.viewport.controls.controls.object.position.copy(new Vector3(0, 50, 0))
      this.viewport.controls.controls.maxAzimuthAngle = 0
      this.viewport.controls.controls.minAzimuthAngle = 0
      this.viewport.controls.controls.maxPolarAngle = 0
      this.viewport.controls.controls.minPolarAngle = 0
      this.viewport.controls.controls.update()
    }
    if (type === '3d'){
      this.viewport.controls.controls.enableRotate = true;
      this.viewport.controls.controls.maxAzimuthAngle = Infinity
      this.viewport.controls.controls.minAzimuthAngle = Infinity
      this.viewport.controls.controls.minPolarAngle = Math.PI / 4
      this.viewport.controls.controls.maxPolarAngle = (Math.PI / 2) * 0.96
      this.viewport.controls.controls.maxDistance = 800
      this.viewport.controls.controls.dampingFactor = 0.05
      this.viewport.controls.controls.minDistance = 1
      this.viewport.controls.controls.update()
    }
  }

  /**
   * 设置是否显示辅助对象
   * @param showSceneHelpers
   */
  setShowHelper(showHelper:boolean){
    this.editor.signals.showHelpersChanged.dispatch(showHelper);
  }
  /**
   * 设置是否显示网格
   * @param showSceneHelpers
   */
  setShowGrid(showGrid:boolean){
    this.editor.signals.showGridChanged.dispatch(showGrid);
  }

  /**
   * 坐标点拾取
   * @param showSceneHelpers
   */
  getPoint(fn:(point:number[])=>void){
    const that = this
    const gp = (event) => {
      const mousePlanePos = that.get_mouse_plane_pos(event);
      that.viewport.dom.removeEventListener('click', gp, false);
      fn([mousePlanePos.x,mousePlanePos.y,mousePlanePos.z])
    }
    this.viewport.dom.addEventListener('click', gp, false);
  }

  /**
   * 获取场景的截图
   * @param showSceneHelpers
   */
  getImage(){
    this.renderer.render(this.editor.scene,this.editor.camera);
    const base64Image = this.renderer.domElement.toDataURL('image/png');

    return base64Image;
  }

  /**
   * 销毁编辑器
   */
  destroy(){
    delete this.components;
    if(this.keyboard){
      this.keyboard.destroy();
    }
    this.editor.clear();
    this.renderer.dispose();
    this.container.removeChild(this.viewport.dom);
    this.container.removeEventListener('dragover',this.onDragover);
    this.container.removeEventListener('drop',this.onDrop);
    this.el.removeChild(this.container);
  }

  /**
   * 注册组件
   * @param compoennts 物体组件数据
   */
  register(compoennts:PandaCommponents){
    if(this.components)
    Object.assign(this.components, compoennts);
  }

  /**
   * 物体组件数据生成三维物体并添加到场景中
   * @param compoennt 网格组件数据
   * @param parent
   * @returns 三维物体
   */

  add(compoennt:PandaCommponent,parent?:THREE.Object3D):THREE.Object3D|undefined{
    if(!this.components){
      throw new Error('无注册的组件');
    }
    const handler=this.components[compoennt.type];
    if(handler===undefined){
      throw new Error(`未知的组件类型: ${compoennt.type}`);
    }
    const object=handler(compoennt);
    if(object instanceof Object3D){
      return this._add(compoennt,object as Object3D);
    }
    if(object instanceof Promise){
      object.then(obj=>{
        this._add(compoennt,obj as Object3D);
      }).catch(e=>{
        throw e;
      });
    }

  }

  private _add(compoennt:PandaCommponent,obj:THREE.Object3D):THREE.Object3D|undefined{
    setBaseAttr(obj,compoennt);
    if(this.onAddObjectBefore){
      const canAdd=this.onAddObjectBefore(compoennt,obj);
      if(canAdd===undefined||canAdd===null||canAdd===false){
        return undefined;
      }
    }
    
    this.editor.execute(new AddObjectCommand(this.editor, obj));
    if(this.onAddObjectAfter){
      this.onAddObjectAfter(compoennt,obj);
    }
    return obj;
  }

  get scene(){
    return this.editor.scene;
  }

  /**
   * uuid获取对象
   * @param uuid Object3D对象的uuid
   * @returns
   */
  getObject(uuid:string){
    return this.editor.scene.getObjectByProperty('uuid',uuid);
  }

  /**
   * 选中物体，传null取消选中
   * @param object uuid或Object3D对象
   */
  select(object?:string|Object3D|null){
    if(object&&typeof object==='string'){
      const target=this.getObject(object);
      this.editor.signals.objectSelected.dispatch(target);
    }else{
      this.editor.signals.objectSelected.dispatch(object);
    }
  }
  /**
   * 聚焦到物体，传null取消选中
   * @param object uuid或Object3D对象
   */
  focus(object?:string|Object3D|null){
    if(object&&typeof object==='string'){
      const target=this.getObject(object);
      this.editor.signals.objectFocused.dispatch(target);
    }else{
      this.editor.signals.objectFocused.dispatch(object);
    }
  }

  isMultiSelect(object?:Object3D){
    if (object == null){
      object = this.editor.selected
    }
    return object != null && object.userData.isMulti ? true : false
  }
  /**
   * 设置动画
   */
  setAnimation(uuid:string,animations:PandaAnimation[]){
    const object=this.editor.scene.getObjectByProperty('uuid',uuid);
    if(object!==undefined){
      if(object.userData===undefined){
        object.userData={};
      }
      object.userData.animations=animations;
    }
  }
  /**
   * 设置事件
   */
  setEvent(uuid:string,events:PandaEvent[]){
    const object=this.editor.scene.getObjectByProperty('uuid',uuid);
    if(object!==undefined){
      if(object.userData===undefined){
        object.userData={};
      }
      object.userData.eventList=events;
    }
  }
  /**
   * 设置连接配置
   */
  setTwinConfig(twinConfig: TwinConfig){
    this.editor.twinConfig = twinConfig
  }
  /**
   * 运行场景
   */
  startPlayer(){
    this.editor.signals.startPlayer.dispatch();
  }
  /**
   * 停止运行场景
   */
  stopPlayer(){
    this.editor.signals.stopPlayer.dispatch();
  }

  moveObject( object:Object3D, newParent?:Object3D, nextObject?:Object3D) {

    if ( nextObject === null ) nextObject = undefined;

    let newParentIsChild = false;

    object.traverse( function ( child ) {
      if ( child === newParent ) newParentIsChild = true;
    } );

    if ( newParentIsChild ) return;

    this.editor.execute( new MoveObjectCommand( this.editor, object, newParent, nextObject ) );
  }

  /**
   * 修改2D文字物体信息
   * @param uuid
   * @param data
   * @returns
   */
  setText(uuid:string,data:PandaCommponent){
    const object=this.editor.scene.getObjectByProperty('uuid',uuid);
    if(!object)return;
    var newData=data;
    if(object.userData.text!==undefined&&object.userData.text!=null){
      newData=Object.assign(object.userData.text,data);
    }
    const text=compoennts.text2d(newData) as Sprite;
    this.editor.execute(new SetMaterialCommand(this.editor,object,text.material,0));
  }
  /**
   * 设置管道的基本参数
   * @param uuid
   * @param data
   * @returns
   */
  setPipeGeometry(uuid:string,data:PandaCommponent){
    const object:any=this.editor.scene.getObjectByProperty('uuid',uuid);
    if(!object)return;
    if(!object.geometry.parameters)return;
    let curve = object.geometry.parameters.path
    let radius = object.geometry.parameters.radius
    if(data.points){
      curve = createPath(data.points)
    }
    if(data.radius){
      radius = data.radius
    }
    this.editor.execute(new SetGeometryCommand(this.editor,object,  new TubeGeometry(curve, 100,  radius, 16, false)));
  }
  /**
   * 设置值
   * @param target uuid或Object3D对象
   * @param key 字段名
   * @param value 值
   */
  setValue<T extends Object3D,K extends keyof T>(target:string|T,key:K,value:any){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      this.editor.execute(new SetValueCommand(this.editor,object,key as any,value));
    }
  }
  //设置材质值
  setMaterialValue<T extends Object3D,K extends keyof T>(target:string|T,key:K,value:any,index:number){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      // 检查是不是Group ，如果是，遍历下面所有的Mesh设置材质
      if (object instanceof THREE.Group){
        const editor = this.editor
        object.traverse(function(child) {
          if (child instanceof THREE.Mesh) {
            if (key as String === 'opacity' ){
              // 判断是否开启transparent
              if ((child.material instanceof Material) && !child.material.transparent){
                editor.execute(new SetMaterialValueCommand(editor,child as Object3D,'transparent',true,index));
              }
            }
            editor.execute(new SetMaterialValueCommand(editor,child as Object3D,key as any,value,index));
          }
        });
      }
      if (object instanceof THREE.Mesh) {
        if (key as String === 'opacity' ){
          // 判断是否开启transparent
          if ((object.material instanceof Material) && !object.material.transparent){
            this.editor.execute(new SetMaterialValueCommand(this.editor,object as Object3D,'transparent',true,index));
          }
        }
        this.editor.execute(new SetMaterialValueCommand(this.editor,object as Object3D,key as any,value,index));
      }
    }
  }
  //设置模型材质
  setMaterial<T extends Object3D,K extends keyof T>(target:string|T,value:any,index:number){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      if (object instanceof THREE.Group){
        const editor = this.editor
        object.traverse(function(child) {
          if (child instanceof THREE.Mesh) {
            editor.execute(new SetMaterialCommand(editor,child as Object3D,value,index));
          }
        });
      }
      if (object instanceof THREE.Mesh) {
        this.editor.execute(new SetMaterialCommand(this.editor,object as Object3D,value,index));
      }
    }
  }
  //设置位置值
  setPositionValue<T extends Object3D,K extends keyof T>(target:string|T,newPos:Vector3){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      this.editor.execute(new SetPositionCommand(this.editor,object,newPos));
    }
  }

  //设置旋转值
  setRotationValue<T extends Object3D,K extends keyof T>(target:string|T,newRot:Euler){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      this.editor.execute(new SetRotationCommand(this.editor,object,newRot));
    }
  }
  //设置缩放值
  setScaleValue<T extends Object3D,K extends keyof T>(target:string|T,newScale:Vector3){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      this.editor.execute(new SetScaleCommand(this.editor,object,newScale));
    }
  }
  // 设置模型是否可拾取
  setRaycast<T extends Object3D>(target:string|T,selectable:boolean){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      object.userData.selectable = selectable
      this.editor.execute(new SetValueCommand(this.editor,object,"userData", object.userData));
    }
  }

   //设置看向摄像头
   setLookCamera<T extends Object3D,K extends keyof T>(target:string|T,type:string){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      if (type === "horizontal" || type === "vertical" ) {
        let exist = false
        this.editor.lookCameraObj.forEach((item,index)=> {
          if (object.uuid === item.object) {
            exist = true
            return
          }
        })
        if (!exist){
          this.editor.lookCameraObj.push({object: object.uuid, type: type})
        }
      }
      if (type === "none"){
        this.editor.lookCameraObj.forEach((item,index)=>{
          if (object.uuid === item.object){
            this.editor.lookCameraObj.splice(index,1)
          }
        })
      }
    }
  }

  //模型后期特效  设置模型线框
  setEdgesObj<T extends Object3D,K extends keyof T>(target:string|T,bool:boolean, param?:LineBasicMaterialParameters){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      let exist = false
      let existIndex = 0
      this.editor.edgesObj.forEach((item,index)=> {
        if (object.uuid === item.object ) {
          exist = true
          existIndex = index
          return
        }
      })
      if (bool) {
        if(!exist && param){
          this.editor.edgesObj.push({object:object.uuid,param: param})
        }
      }else{
        if (exist) {
          this.editor.edgesObj.splice(existIndex,1)
        }
      }
    }
  }

  //模型后期特效  设置模型发光
  setOutlineObj<T extends Object3D,K extends keyof T>(target:string|T,bool:boolean){
    let object:Object3D|null=null;
    if(target instanceof String){
      object=this.editor.scene.getObjectByProperty('uuid',target);
    }else if(target instanceof Object3D){
      object=target;
    }
    if(object!==null){
      let exist = false
      let existIndex = 0
      this.editor.outlineObj.forEach((item,index)=> {
        if (object.uuid === item ) {
          exist = true
          existIndex = index
          return
        }
      })
      if (bool) {
        if(!exist){
          this.editor.outlineObj.push(object.uuid)
        }
      }else{
        if (exist) {
          this.editor.outlineObj.splice(existIndex,1)
        }
      }
    }
  }

  /**
   * 选中物体对象事件
    * @param fn
    */
 onSelected(fn:(object:Object3D)=>void){
    this.editor.signals.objectSelected.add(fn);
 }

  get control():Control{
    return this.editor.options.control;
  }
  set control(control: Control){
    this.editor.options.control=control;
  }


  get data():Uint8Array{
    return compress(JSON.stringify(this.editor.toJSON(),(key, value) => {
      if(key.startsWith('_')){
        return;
      }
      try{
        if (value instanceof Number) {
          return parseFloat(value.toFixed(2));
        }
      }catch(e){
        console.error(e);
      }
      return value;
    },0));
  }

  open(data?:Uint8Array){
    if(data){
      const json=new TextDecoder().decode(decompress(data));
      try {
        this.openJson(JSON.parse(json));
      } catch (error) {
        throw error;
      }
    }else{
      throw new Error('不能打开空数据');
    }
  }

  openJson(json:any){
    this.editor.fromJSON(json);
  }

  load(){
    const fileInput = document.createElement( 'input' );
		fileInput.multiple = false;
		fileInput.type = 'file';
		fileInput.accept='.px';
		fileInput.addEventListener( 'change', ()=> {
          const fr=new FileReader();
          fr.onload=(ev)=>{
            this.open(new Uint8Array(ev.target.result as ArrayBuffer));
          }
          fr.readAsArrayBuffer(fileInput.files[0]);
		} );
		fileInput.click();
  }

  download(filename:string='panda'){
    const result=this.data;
    const blob = new Blob([result], { type: 'text/plain' });
    const downloadUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;
    downloadLink.download = filename+'.px';
    downloadLink.click();
    URL.revokeObjectURL(downloadUrl);
  }

  get_mouse_plane_pos(evt) {
    const mouse = new Vector2();
    const raycaster = new THREE.Raycaster();
    evt.preventDefault();
    const rect = this.renderer.domElement.getBoundingClientRect();
    mouse.x = ((evt.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((evt.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, this.editor.camera); // 通过摄像机和鼠标位置更新射线
    const intersection = new THREE.Vector3();
    const Plane = new THREE.Plane(new THREE.Vector3(0, 0.01, 0), 0);
    if (raycaster.ray.intersectPlane(Plane, intersection)) {
      return intersection;
    }
    return null;
  }

  /**
   * 拖动事件dataTransfer
   */
  static readonly DargData='Panda';
  static draggable(dom:HTMLElement,data:any){
    dom.setAttribute('draggable','true');
    dom.ondragstart=(e:DragEvent)=>{
        e.dataTransfer?.setData(PandaEngine.DargData,JSON.stringify(data));
    }
  }
  /**
   * 创建材质
   * @param materialType 材质类型
   * @param material 材质参数
   * @returns
   */
  static createMaterial(materialType?:MaterialType,material?:MaterialParameters):Material|undefined{
    materialType=materialType||MaterialType.Standard;
    if(materialType===MaterialType.Basic){
        return new MeshBasicMaterial(material);
    }
    if(materialType===MaterialType.Standard){
        return new MeshStandardMaterial(material);
    }
    if(materialType===MaterialType.Lambert){
        return new MeshLambertMaterial(material);
    }
  }
  static MaterialType=MaterialType;
  static THREE=THREE;
}

(window as any).PandaEngine=PandaEngine;

function setBaseAttr(object:THREE.Object3D,data:PandaCommponent){
  if(data.userData){
    object.userData=data.userData;
  }else {
    object.userData= {};
  }
  if(data.position){
    object.position.copy(data.position);
  }
  if(data.scale){
    object.scale.copy(data.scale);
  }
  if(data.name){
    object.name=data.name;
  }
  if(data.animations){
    object.userData.animations=data.animations;
  }
  if(data.events){
    object.userData.eventList=data.events;
  }
}

interface PandaCommponents{
  [key:string]:(componentData:PandaCommponent)=>THREE.Object3D|Promise<THREE.Object3D>
}
export interface PandaCommponent{
  type:string;
  material?:THREE.MaterialParameters;
  materialType?:MaterialType;

  [key:string]:any;
}

const defaultComponents={...compoennts};


export interface PandaAnimation{
  key:string,
  to:any,
  repeat?:number,
  start?:boolean,
  delay?:number,
  yoyo?:boolean,
  easing?:string
}

export interface PandaEvent{
  action: string,  //执行动作
  name: string,
  type: string,    //事件类型
  [key:string]:any;
}

export interface Control {
  enabled: boolean
  /**
     * How far you can dolly in ( PerspectiveCamera only ).
     * @default 0
     */
   minDistance?: number;

   /**
    * How far you can dolly out ( PerspectiveCamera only ).
    * @default Infinity
    */
   maxDistance?: number;

   /**
    * How far you can zoom in ( OrthographicCamera only ).
    * @default 0
    */
   minZoom?: number;

   /**
    * How far you can zoom out ( OrthographicCamera only ).
    * @default Infinity
    */
   maxZoom?: number;

   /**
    * How far you can orbit vertically, lower limit.
    * Range is 0 to Math.PI radians.
    * @default 0
    */
   minPolarAngle?: number;

   /**
    * How far you can orbit vertically, upper limit.
    * Range is 0 to Math.PI radians.
    * @default Math.PI.
    */
   maxPolarAngle?: number;

   /**
    * How far you can orbit horizontally, lower limit.
    * If set, the interval [ min, max ]
    * must be a sub-interval of [ - 2 PI, 2 PI ],
    * with ( max - min < 2 PI ).
    * @default Infinity
    */
   minAzimuthAngle?: number;

   /**
    * How far you can orbit horizontally, upper limit.
    * If set, the interval [ min, max ] must be a sub-interval
    * of [ - 2 PI, 2 PI ], with ( max - min < 2 PI ).
    * @default Infinity
    */
   maxAzimuthAngle?: number;

   /**
    * Set to true to enable damping (inertia), which can
    * be used to give a sense of weight to the controls.
    * Note that if this is enabled, you must call
    * .update () in your animation loop.
    * @default false
    */
   enableDamping?: boolean;

   /**
    * The damping inertia used if .enableDamping is set to true.
    * Note that for this to work,
    * you must call .update () in your animation loop.
    * @default 0.05
    */
   dampingFactor?: number;

   /**
    * Enable or disable zooming (dollying) of the camera.
    * @default true
    */
   enableZoom?: boolean;

   /**
    * Speed of zooming / dollying.
    * @default 1
    */
   zoomSpeed?: number;

   /**
    * Setting this property to `true` allows to zoom to the cursor's position.
    * @default false
    */
   zoomToCursor?: boolean;

   /**
    * Enable or disable horizontal and
    * vertical rotation of the camera.
    * Note that it is possible to disable a single axis
    * by setting the min and max of the polar angle or
    * azimuth angle to the same value, which will cause
    * the vertical or horizontal rotation to be fixed at that value.
    * @default true
    */
   enableRotate?: boolean;

   /**
    * Speed of rotation.
    * @default 1
    */
   rotateSpeed?: number;

   /**
    * Enable or disable camera panning.
    * @default true
    */
   enablePan?: boolean;

   /**
    * Speed of panning.
    * @default 1
    */
   panSpeed?: number;

   /**
    * Defines how the camera's position is translated when panning.
    * If true, the camera pans in screen space. Otherwise,
    * the camera pans in the plane orthogonal to the camera's
    * up direction. Default is true for OrbitControls; false for MapControls.
    * @default true
    */
   screenSpacePanning?: boolean;

   /**
    * How fast to pan the camera when the keyboard is used.
    * Default is 7.0 pixels per keypress.
    * @default 7
    */
   keyPanSpeed?: number;

   /**
    * Set to true to automatically rotate around the target.
    * Note that if this is enabled, you must call
    * .update () in your animation loop.
    */
   autoRotate?: boolean;

   /**
    * How fast to rotate around the target if .autoRotate is true.
    * Default is 2.0, which equates to 30 seconds per orbit at 60fps.
    * Note that if .autoRotate is enabled, you must call
    * .update () in your animation loop.
    * @default 2
    */
   autoRotateSpeed?: number;
}

export interface TwinConfig {
  twinType: string // 孪生类型
  url: string, // 路径
  screenId?: string // 画布ID
  username?: string // mqtt 账号
  password?: string // mqtt 密码
  topic?: string // mqtt 订阅topic
  pubTopic?: string // mqtt 推送topic
}


/*

function runSyncFetch(func:Function){
  const cache = [];
  let i = 0;
  const _originalFetch = window.fetch;
  window.fetch = (input: RequestInfo | URL, init?: RequestInit)=>{

    if(cache[i]){
      if(cache[i].status === 'fulfilled'){
        return cache[i].data
      }
      else if(cache[i].status === 'rejected'){
        throw cache[i].err
      }
    }

    const result = {
      status:'padding',
      data:null,
      err:null
    };
    cache[i++] = result;

    const prom =  _originalFetch(input,init).then( res=>res.arrayBuffer()).then(
    res=>{
      result.status = 'fulfilled';
      result.data = res;
    },err=>{
      result.status = 'rejected';
      result.data = err;
    }
    )

    throw prom
  };
  try{
    func()
  }catch(err){

    if(err instanceof Promise){
      const reRun = ()=>{
        i=0;
        func()
      }
      err.then(reRun,reRun)
    }
  }finally{
    // window.fetch=_originalFetch;
  }

}
*/
